import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/States";
import api, { humanError } from "@/lib/api";
import { toast } from "sonner";

const NET_TONE = {
  instagram: "bg-[#F58529]/15 text-[#8A4900]",
  linkedin: "bg-[#0A66C2]/15 text-[#0A66C2]",
  facebook: "bg-[#1877F2]/15 text-[#1877F2]",
  x: "bg-black/10 text-black",
};

const VIEWS = [
  { id: "month", label: "Month" },
  { id: "week", label: "Week" },
  { id: "day", label: "Day" },
];

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const startOfDay = (d) => { const n = new Date(d); n.setHours(0, 0, 0, 0); return n; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const isSameDay = (a, b) => dayKey(a) === dayKey(b);
const hhmm = (d) => d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export default function CalendarPage() {
  const [view, setView] = useState("month");
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [items, setItems] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  // The visible window, which is also the range the API is asked for.
  const { start, end, days } = useMemo(() => {
    if (view === "day") {
      const s = startOfDay(anchor);
      return { start: s, end: addDays(s, 1), days: [s] };
    }
    if (view === "week") {
      const s = addDays(startOfDay(anchor), -anchor.getDay());
      return { start: s, end: addDays(s, 7), days: Array.from({ length: 7 }, (_, i) => addDays(s, i)) };
    }
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const gridStart = addDays(startOfDay(first), -first.getDay());
    const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    const cellCount = Math.ceil((first.getDay() + last.getDate()) / 7) * 7;
    return {
      start: gridStart,
      end: addDays(gridStart, cellCount),
      days: Array.from({ length: cellCount }, (_, i) => addDays(gridStart, i)),
    };
  }, [view, anchor]);

  const load = useCallback(async () => {
    try {
      const [p, j] = await Promise.all([
        api.get(`/schedule/calendar?start=${encodeURIComponent(start.toISOString())}&end=${encodeURIComponent(end.toISOString())}`),
        api.get("/schedule/jobs?limit=200"),
      ]);
      setItems(p.data.items);
      setJobs(j.data.items);
    } catch (e) {
      setItems([]);
      setJobs([]);
      toast.error(humanError(e));
    }
  }, [start, end]);

  useEffect(() => { setItems(null); load(); }, [load]);

  const byDay = useMemo(() => {
    const map = {};
    (items || []).forEach((p) => {
      const k = dayKey(new Date(p.scheduled_at));
      (map[k] = map[k] || []).push(p);
    });
    Object.values(map).forEach((list) =>
      list.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)));
    return map;
  }, [items]);

  /**
   * Move a post to a new slot.
   *
   * Dropping on a day keeps the post's existing time of day: someone shifting
   * a post to Thursday means the same slot on Thursday, not midnight.
   * Dropping on an hour row in the day view sets that hour instead.
   */
  async function reschedule(postId, targetDay, hour) {
    const post = (items || []).find((p) => p.id === postId);
    if (!post) return;
    const existing = new Date(post.scheduled_at);
    const next = new Date(targetDay);
    if (hour === undefined) next.setHours(existing.getHours(), existing.getMinutes(), 0, 0);
    else next.setHours(hour, 0, 0, 0);

    if (next.getTime() === existing.getTime()) return;

    // Move it in the UI first, then reconcile with the server.
    setItems((cur) => cur.map((p) => (p.id === postId ? { ...p, scheduled_at: next.toISOString() } : p)));
    try {
      await api.post(`/posts/${postId}/schedule`, { run_at: next.toISOString() });
      toast.success(`Moved to ${next.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })} at ${hhmm(next)}`);
      load();
    } catch (e) {
      toast.error(humanError(e));
      load();
    }
  }

  function onDrop(e, targetDay, hour) {
    e.preventDefault();
    setDropTarget(null);
    const id = e.dataTransfer.getData("text/plain") || dragId;
    setDragId(null);
    if (id) reschedule(id, targetDay, hour);
  }

  function step(dir) {
    setAnchor((a) => {
      if (view === "day") return addDays(a, dir);
      if (view === "week") return addDays(a, dir * 7);
      const n = new Date(a); n.setMonth(n.getMonth() + dir); return n;
    });
  }

  const heading = view === "day"
    ? anchor.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : view === "week"
      ? `${days[0].toLocaleDateString(undefined, { day: "numeric", month: "short" })} to ${days[6].toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`
      : anchor.toLocaleString(undefined, { month: "long", year: "numeric" });

  const chip = (p) => (
    <div
      key={p.id}
      draggable
      onDragStart={(e) => { setDragId(p.id); e.dataTransfer.setData("text/plain", p.id); e.dataTransfer.effectAllowed = "move"; }}
      onDragEnd={() => { setDragId(null); setDropTarget(null); }}
      data-testid={`cal-post-${p.id}`}
      className={`cursor-grab rounded-md bg-[#EEF3FB] px-2 py-1 text-xs font-semibold text-[#1F5FD8] hover:bg-[#DDE7FA] active:cursor-grabbing ${dragId === p.id ? "opacity-40" : ""}`}
      title={`${p.title} — drag to reschedule`}
    >
      <Link to={`/app/composer/${p.id}`} className="block truncate" draggable={false}>
        {hhmm(new Date(p.scheduled_at))} · {p.title}
      </Link>
    </div>
  );

  const dropProps = (key, day, hour) => ({
    onDragOver: (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDropTarget(key); },
    onDragLeave: () => setDropTarget((t) => (t === key ? null : t)),
    onDrop: (e) => onDrop(e, day, hour),
  });

  const failedCount = jobs.filter((j) => j.status === "failed" || j.status === "dead_letter").length;

  return (
    <div>
      <PageHeader
        title="Calendar"
        subtitle="Everything scheduled and published. Drag a post to move it."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-full border border-[#E4E9EF] p-0.5" role="tablist" aria-label="Calendar view">
              {VIEWS.map((v) => (
                <button
                  key={v.id} role="tab" aria-selected={view === v.id}
                  onClick={() => setView(v.id)}
                  data-testid={`calendar-view-${v.id}`}
                  className={`rounded-full px-3 py-1.5 text-sm font-semibold transition ${view === v.id ? "bg-[#1F5FD8] text-white" : "text-[#43535E] hover:bg-[#F6F8FA]"}`}
                >{v.label}</button>
              ))}
            </div>
            <button onClick={() => step(-1)} aria-label="Previous" className="rounded-full border border-[#E4E9EF] p-2 hover:bg-[#F6F8FA]" data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></button>
            <div className="min-w-[190px] text-center text-sm font-semibold text-[#0B2B33]">{heading}</div>
            <button onClick={() => step(1)} aria-label="Next" className="rounded-full border border-[#E4E9EF] p-2 hover:bg-[#F6F8FA]" data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></button>
            <button onClick={() => setAnchor(startOfDay(new Date()))} className="rounded-full border border-[#E4E9EF] px-3 py-2 text-sm hover:bg-[#F6F8FA]" data-testid="calendar-today">Today</button>
            <button onClick={load} aria-label="Refresh" className="rounded-full border border-[#E4E9EF] px-3 py-2 text-sm hover:bg-[#F6F8FA]" data-testid="calendar-refresh"><RefreshCw className="h-4 w-4" /></button>
          </div>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="rounded-xl border border-[#E4E9EF] bg-white p-4" data-testid="calendar-grid">
          {items === null ? (
            <div className="grid grid-cols-7 gap-2">
              {Array.from({ length: 35 }).map((_, i) => <div key={i} className="h-24 animate-pulse rounded-lg bg-[#F6F8FA]" />)}
            </div>
          ) : view === "day" ? (
            <div className="max-h-[640px] overflow-y-auto">
              {Array.from({ length: 24 }, (_, h) => {
                const posts = (byDay[dayKey(days[0])] || []).filter((p) => new Date(p.scheduled_at).getHours() === h);
                const k = `h-${h}`;
                return (
                  <div key={h} {...dropProps(k, days[0], h)}
                    className={`flex gap-3 border-b border-[#E4E9EF] py-1.5 last:border-b-0 ${dropTarget === k ? "bg-[#EEF3FB]" : ""}`}>
                    <div className="w-14 shrink-0 pt-1 text-right text-xs tabular-nums text-[#8A97A0]">
                      {String(h).padStart(2, "0")}:00
                    </div>
                    <div className="flex min-h-8 flex-1 flex-wrap gap-1">{posts.map((p) => chip(p))}</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              <div className="mb-2 grid grid-cols-7 gap-2 text-center text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">
                {DAY_NAMES.map((d) => <div key={d}>{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-2">
                {days.map((day) => {
                  const posts = byDay[dayKey(day)] || [];
                  const outside = view === "month" && day.getMonth() !== anchor.getMonth();
                  const today = isSameDay(day, new Date());
                  const k = dayKey(day);
                  return (
                    <div key={k} {...dropProps(k, day)}
                      className={`rounded-lg border p-2 ${view === "week" ? "min-h-[380px]" : "min-h-24"} ${outside ? "bg-[#F6F8FA]/60" : "bg-white"} ${today ? "border-[#1F5FD8]" : "border-[#E4E9EF]"} ${dropTarget === k ? "border-[#1F5FD8] bg-[#EEF3FB] ring-2 ring-[#1F5FD8]/20" : ""}`}>
                      <div className={`mb-1 text-xs font-semibold ${today ? "text-[#1F5FD8]" : outside ? "text-[#B6C0C8]" : "text-[#43535E]"}`}>
                        {view === "week" ? `${DAY_NAMES[day.getDay()]} ${day.getDate()}` : day.getDate()}
                      </div>
                      <div className="space-y-1">
                        {(view === "week" ? posts : posts.slice(0, 3)).map((p) => chip(p))}
                        {view === "month" && posts.length > 3
                          ? <div className="text-[10px] text-[#8A97A0]">+ {posts.length - 3} more</div>
                          : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <aside className="space-y-4">
          <div className="rounded-xl border border-[#E4E9EF] bg-white p-4" data-testid="jobs-panel">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-sm font-semibold text-[#0B2B33]">Publish queue</div>
              <span className="hs-chip">{jobs.length}</span>
            </div>
            {failedCount > 0 ? (
              <div className="mb-2 rounded-lg bg-[#FDECEC] px-3 py-2 text-xs font-semibold text-[#B02328]">
                {failedCount} {failedCount === 1 ? "job needs" : "jobs need"} attention
              </div>
            ) : null}
            {jobs.length === 0 ? <EmptyState title="Queue is empty" body="Scheduled posts will appear here." /> : (
              <ul className="max-h-[420px] space-y-2 overflow-auto pr-1">
                {jobs.slice(0, 30).map((j) => (
                  <li key={j.id} className="rounded-lg border border-[#E4E9EF] p-2">
                    <div className="flex items-center justify-between">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ${NET_TONE[j.variant_network] || "bg-[#F6F8FA] text-[#43535E]"}`}>{j.variant_network}</span>
                      <span className="text-[11px] text-[#8A97A0]">{j.status}</span>
                    </div>
                    <div className="mt-1 text-xs text-[#43535E]">Run: {new Date(j.run_at).toLocaleString()}</div>
                    {j.last_error ? <div className="mt-1 line-clamp-2 text-[11px] text-[#B02328]">{j.last_error}</div> : null}
                    {(j.status === "failed" || j.status === "dead_letter") ? (
                      <button data-testid={`retry-${j.id}`}
                        onClick={async () => {
                          try { await api.post(`/schedule/jobs/${j.id}/retry`); toast.success("Requeued"); load(); }
                          catch (e) { toast.error(humanError(e)); }
                        }}
                        className="mt-1 text-[11px] font-semibold text-[#1F5FD8] hover:underline">Retry now</button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
